/**
 * Centralized Xano API Client
 * Handles all communication with Xano backend for the main API endpoints
 */

import { XANO_HOST } from '../../shared/constants';

// API Configuration
const XANO_BASE_URL = XANO_HOST;
// Empty = the workspace's LIVE branch, which is what we want in every environment.
// This previously defaulted to 'v2' (the old standalone Qbank instance's live branch).
// After the move into Aksanio workspace 2 the live branch is v1, so a hardcoded ':v2'
// suffix 404s every request. Leave the default empty and let Xano resolve live.
const XANO_BRANCH = import.meta.env.VITE_XANO_BRANCH || '';
const BRANCH_SUFFIX = XANO_BRANCH ? `:${XANO_BRANCH}` : '';
const MAIN_API_BASE = `/api:h-QoQY2S${BRANCH_SUFFIX}`; // Main API group
const AUTH_API_BASE = `/api:IvFHo60M${BRANCH_SUFFIX}`; // Auth API group

interface XanoResponse<T = any> {
  items?: T[];
  itemsReceived?: number;
  curPage?: number;
  nextPage?: number | null;
  prevPage?: number | null;
  itemsTotal?: number;
  [key: string]: any;
}

class XanoApiClient {
  private getAuthToken(): string | null {
    // Get from localStorage where it's stored by the auth system
    return localStorage.getItem('xano_auth_token');
  }

  private getHeaders(requireAuth: boolean = true): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (requireAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    return headers;
  }

  async request<T = any>(
    endpoint: string, 
    options: RequestInit = {}, 
    requireAuth: boolean = true
  ): Promise<T> {
    const url = `${XANO_BASE_URL}${endpoint}`;
    
    try {
      const headers = {
        ...this.getHeaders(requireAuth),
        ...options.headers,
      };
      
      // Debug log
      if (requireAuth && !headers.hasOwnProperty('Authorization')) {
        // No auth token found for authenticated request
      }
      
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // If unauthorized, log more details
        if (response.status === 401) {
          // Unauthorized request
        }
        
        throw new Error(errorData.message || `API Error: ${response.status} ${response.statusText}`);
      }

      return response.json();
    } catch (error: any) {
      // Xano API request failed
      throw error;
    }
  }

  // Main API methods (require auth)
  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<T> {
    let queryString = '';
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      
      // Handle arrays properly for Xano API
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, append each value with the same key
          value.forEach(v => searchParams.append(key.replace('[]', ''), v));
        } else if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      
      const paramsString = searchParams.toString();
      queryString = paramsString ? '?' + paramsString : '';
    }
    
    return this.request<T>(`${MAIN_API_BASE}${endpoint}${queryString}`, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(`${MAIN_API_BASE}${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async patch<T = any>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(`${MAIN_API_BASE}${endpoint}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(`${MAIN_API_BASE}${endpoint}`, {
      method: 'DELETE',
    });
  }

  // Auth API methods
  async authPost<T = any>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(`${AUTH_API_BASE}${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, false);
  }

  async authGet<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(`${AUTH_API_BASE}${endpoint}`, { method: 'GET' });
  }
}

export const xanoClient = new XanoApiClient();